import { useEffect, useState } from "react";
import { IndianRupee, Users, PauseCircle, CalendarCheck, X, ChevronRight, ClipboardList, Check, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api, inr, currentMonth, formatApiError } from "../../lib/api";
import { MonthNav } from "../../components/MonthNav";
import { TaskTemplatesModal } from "../../components/TaskTemplatesModal";
import { useTaxonomy } from "../../lib/useTaxonomy";

const FEE_LABEL = { group: "Group Sessions", transport: "Transport", pt: "Personal Training", previous_dues: "Previous Dues", registration: "Registration Fee" };

// Immediate action items that appear ABOVE the financials whenever a new
// player has been added to the DB. One block per newly-added player with
// their four onboarding to-dos; each can be ticked off individually and the
// whole block disappears when the last task is done.
const OnboardingWidget = () => {
  const [groups, setGroups] = useState(null);
  const [manage, setManage] = useState(false);

  const load = () => api.get("/admin/onboarding").then((r) => setGroups(r.data)).catch(() => setGroups([]));
  useEffect(() => { load(); }, []);

  const complete = async (taskId) => {
    try { await api.post(`/admin/onboarding/${taskId}/done`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const dismissAll = async (playerId) => {
    try { await api.post(`/admin/onboarding/player/${playerId}/dismiss`); toast.success("Marked all done"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const manageBtn = (
    <button onClick={() => setManage(true)} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50 hover:text-[#D4AF37] border border-white/15 px-3 py-1.5 transition-colors duration-150" data-testid="manage-task-templates">
      <Settings2 size={12} /> Manage Task Templates
    </button>
  );
  const modal = manage && <TaskTemplatesModal onClose={() => setManage(false)} />;

  if (!groups) return null;
  if (groups.length === 0) return <div className="flex justify-end" data-testid="onboarding-empty">{manageBtn}{modal}</div>;
  return (
    <section className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5 sm:p-6" data-testid="onboarding-widget">
      {modal}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <ClipboardList size={16} className="text-[#D4AF37]" />
        <h2 className="font-heading text-sm uppercase tracking-[0.25em] text-[#D4AF37] font-bold">Immediate Tasks</h2>
        <span className="text-white/40 text-xs">· {groups.length} new {groups.length === 1 ? "player" : "players"}</span>
        <span className="ml-auto">{manageBtn}</span>
      </div>
      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g.player_id} className="border border-white/10 bg-[#0A0A0A] p-4" data-testid={`onboarding-player-${g.player_id}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-sm">{g.player_name} <span className="text-[#D4AF37] text-xs ml-1">{g.player_id}</span></p>
              <button onClick={() => dismissAll(g.player_id)}
                className="text-[10px] uppercase tracking-widest text-white/40 hover:text-white"
                data-testid={`onboarding-dismiss-${g.player_id}`}>Dismiss all</button>
            </div>
            <ul className="space-y-2">
              {g.tasks.map((t) => (
                <li key={t.id} className="flex items-start gap-3">
                  <button onClick={() => complete(t.id)}
                    className="mt-0.5 w-5 h-5 border border-white/25 flex items-center justify-center hover:border-[#D4AF37] text-[#D4AF37] shrink-0"
                    data-testid={`onboarding-task-${t.id}`} title="Mark done">
                    <Check size={12} className="opacity-0 group-hover:opacity-100" />
                  </button>
                  <span className="text-white/80 text-sm">{t.label}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

const Stat = ({ icon: Icon, label, value, testid, gold, onClick }) => (
  <button onClick={onClick} disabled={!onClick}
    className={`border border-white/10 bg-[#0A0A0A] p-6 text-left transition-colors duration-150 ${onClick ? "hover:border-[#D4AF37] cursor-pointer" : "cursor-default"}`}
    data-testid={testid}>
    <div className="flex items-center gap-3 text-white/40 text-xs uppercase tracking-widest">
      <Icon size={15} className="text-[#D4AF37]" /> {label}
      {onClick && <ChevronRight size={12} className="ml-auto text-white/30" />}
    </div>
    <p className={`font-heading text-3xl font-black mt-3 ${gold ? "text-[#D4AF37]" : "text-white"}`}>{value}</p>
  </button>
);

const DetailDrawer = ({ month, centre, bucket, onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/overview/detail", { params: { month, bucket, ...(centre ? { centre } : {}) } })
      .then((r) => setRows(r.data.rows)).catch(() => setRows([]));
  }, [month, centre, bucket]);
  const title = { billed: "Billed Players", collected: "Players Who Paid", outstanding: "Players With Outstanding",
    prev_outstanding: "Carried-Over From Prior Months", players_with_dues: "Players With Dues",
    active_players: "Active Players", paused: "Players On Hold" }[bucket] || bucket;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="overview-drawer">
      <div className="max-w-2xl mx-auto my-10 bg-[#0A0A0A] border border-white/10 p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">{month}{centre ? ` · ${centre}` : ""}</p>
            <h2 className="font-heading text-xl font-black mt-1">{title} <span className="text-[#D4AF37]">({rows?.length ?? "…"})</span></h2>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="overview-drawer-close"><X size={20} /></button>
        </div>
        {rows === null && <p className="text-white/40 text-sm">Loading…</p>}
        {rows && rows.length === 0 && <p className="text-white/40 text-sm border border-white/10 p-6 text-center">No players in this bucket.</p>}
        <div className="space-y-2">
          {rows?.map((r) => (
            <div key={r.player.player_id} className="flex items-center justify-between border border-white/10 bg-[#050505] p-4" data-testid={`drawer-row-${r.player.player_id}`}>
              <div>
                <p className="font-semibold text-sm">{r.player.name} <span className="text-[#D4AF37] text-xs ml-1">{r.player.player_id}</span></p>
                <p className="text-white/40 text-xs mt-0.5">{r.player.batch || "No batch"}{r.player.centre ? ` · ${r.player.centre}` : ""}</p>
              </div>
              {r.amount > 0 && <p className="font-heading font-black text-[#D4AF37]">{inr(r.amount)}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Overview() {
  const [month, setMonth] = useState(currentMonth());
  const [centre, setCentre] = useState("");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [drawer, setDrawer] = useState(null);
  const { centreNames } = useTaxonomy();

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    api.get("/admin/overview", { params: { month, ...(centre ? { centre } : {}) } })
      .then((r) => { if (!cancelled) { setData(r.data); setStatus("ready"); } })
      .catch(() => { if (!cancelled) setStatus("error"); });
    return () => { cancelled = true; };
  }, [month, centre]);

  return (
    <div data-testid="admin-overview">
      <div className="flex flex-wrap items-center justify-between gap-6 mb-8">
        <MonthNav month={month} onChange={setMonth} testid="overview-month-nav" />
        <div className="flex gap-2 flex-wrap" data-testid="centre-selector">
          {["", ...centreNames].map((c) => {
            const label = c || "All Centres";
            const slug = !c ? "all" : c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            return (
              <button key={c || "all"} onClick={() => setCentre(c)}
                data-testid={`centre-${slug}`}
                className={`px-4 py-2.5 text-xs font-bold uppercase tracking-widest border transition-colors duration-200 ${centre === c ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/20 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {status === "loading" && !data && (
        <div className="space-y-px" data-testid="overview-loading">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[0,1,2,3].map((i) => (
              <div key={i} className="bg-[#0A0A0A] p-6">
                <div className="h-3 w-24 bg-white/10 animate-pulse" />
                <div className="h-8 w-32 bg-white/10 mt-4 animate-pulse" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 border-t-0">
            {[0,1,2,3].map((i) => (
              <div key={i} className="bg-[#0A0A0A] p-6">
                <div className="h-3 w-24 bg-white/10 animate-pulse" />
                <div className="h-8 w-24 bg-white/10 mt-4 animate-pulse" />
              </div>
            ))}
          </div>
          <p className="text-white/40 text-xs uppercase tracking-widest mt-6" data-testid="overview-loading-hint">Loading dashboard…</p>
        </div>
      )}

      {status === "error" && !data && (
        <div className="border border-red-500/30 bg-red-500/5 p-6 text-white/70 text-sm" data-testid="overview-error">
          Couldn't load the dashboard. Check your connection and try again.
        </div>
      )}

      {data && (
        <>
          <OnboardingWidget />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            <Stat icon={IndianRupee} label="Billed" value={inr(data.billed)} testid="stat-billed" onClick={() => setDrawer("billed")} />
            <Stat icon={IndianRupee} label="Collected" value={inr(data.collected)} testid="stat-collected" onClick={() => setDrawer("collected")} />
            <Stat icon={IndianRupee} label="Outstanding" value={inr(data.outstanding)} testid="stat-outstanding" gold onClick={() => setDrawer("outstanding")} />
            <Stat icon={Users} label="Players With Dues" value={data.players_with_dues} testid="stat-with-dues" gold onClick={() => setDrawer("players_with_dues")} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 border-t-0">
            <Stat icon={IndianRupee} label="Carried Over" value={inr(data.prev_outstanding || 0)} testid="stat-prev-outstanding" gold onClick={() => setDrawer("prev_outstanding")} />
            <Stat icon={Users} label="Active Players" value={data.active_players} testid="stat-active" onClick={() => setDrawer("active_players")} />
            <Stat icon={PauseCircle} label="On Hold" value={data.paused} testid="stat-paused" onClick={() => setDrawer("paused")} />
            <Stat icon={CalendarCheck} label="Sessions Held" value={data.sessions_this_month} testid="stat-sessions" />
          </div>

          <h2 className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mt-10 mb-4">Fee-Type Breakdown</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10" data-testid="fee-type-breakdown">
            {["group", "transport", "pt", "registration", "previous_dues"].map((t) => {
              const bt = data.per_type?.[t] || { billed: 0, collected: 0 };
              const out = Math.max(0, bt.billed - bt.collected);
              return (
                <div key={t} className="bg-[#0A0A0A] p-5" data-testid={`fee-type-${t}`}>
                  <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">{FEE_LABEL[t]}</p>
                  <p className="font-heading text-xl font-black text-white">{inr(bt.billed)}</p>
                  <div className="text-xs text-white/50 mt-2 space-y-0.5">
                    <p>Collected <span className="text-white">{inr(bt.collected)}</span></p>
                    <p className={out > 0 ? "text-[#D4AF37]" : "text-white/40"}>Outstanding {inr(out)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {data.billed === 0 && (
            <p className="text-white/40 text-sm mt-8 border border-white/10 bg-[#0A0A0A] p-6" data-testid="overview-empty-hint">
              No fees billed for this month yet. Set up fee plans under Players → select a player → Fee Plans.
            </p>
          )}
        </>
      )}

      {drawer && <DetailDrawer month={month} centre={centre} bucket={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}
